/*
 * playbookReference — shared, isomorphic reference content for the profession
 * playbook, used by BOTH the /v2 funnel page (components/home/HomeV2.jsx) and
 * the "email me my playbook" email (server/utils/playbookEmailTemplate.js). Pure
 * data + plain text — no React, no Node deps — so both surfaces import the SAME
 * source of truth. Edit a stat here once and it updates the page and the email.
 *
 * CommonJS on purpose: loads in webpack (frontend, via named-import interop) AND
 * in the Node backend natively, without depending on the server transpile hook.
 *
 * ACCURACY: the per-platform numbers are hedged, approximate industry averages
 * for BUSINESS accounts, from the researched social-platform-logistics data
 * (project_pdf_guide_social_platforms memory). Always present them as "approximate
 * / industry average / yours will vary" — never as guarantees.
 */

// Per-platform "how the algorithm treats your post":
//   reach     — how many of YOUR followers actually see a post
//   reachNote — the one-line why
//   life      — how long the post keeps earning reach
//   boost     — what makes it get shown to MORE people
//   cadence   — how often to post there
//   discovery — how new people find it (search / hashtags / none)
//   tip       — one plain-language move
const PLATFORM_PLAYBOOK_INFO = {
  facebook: {
    reach: '~2–6% of followers',
    reachNote: 'Meta throttles organic reach to push paid boosts.',
    life: 'Half its reach in ~90 min — mostly done within a day',
    boost: 'Early comments & shares, and you replying in the first hour',
    cadence: '3–5× a week',
    discovery: 'Hashtags barely help — your page name & reviews do the SEO work',
    tip: 'Ask a question — Facebook rewards posts that spark conversation.',
  },
  instagram: {
    reach: '~9–12% of followers (feed)',
    reachNote: 'Saves and shares are the strongest ranking signals.',
    life: 'Feed posts ~1–2 days; Reels can resurface for weeks',
    boost: 'Saves, shares to Stories, and watch-through on Reels',
    cadence: '4–5× a week + daily Stories',
    discovery: 'Keyword search + 3–5 specific hashtags (skip the giant ones)',
    tip: 'Make it worth saving — checklists, before/afters, how-tos.',
  },
  tiktok: {
    reach: 'Followers barely matter',
    reachNote: 'Shown to an interest graph — a new post is tested on strangers.',
    life: 'Days to weeks; year-old videos can blow up again',
    boost: 'Watch time, rewatches and completion rate',
    cadence: 'Daily if you can — at least 4–5× a week',
    discovery: 'Search + hashtags + the words you say and put on screen',
    tip: 'Hook in the first 2 seconds; keep it short enough to replay.',
  },
  youtube: {
    reach: 'Search + suggestions, not followers',
    reachNote: 'Built for the long tail — videos earn views for months.',
    life: 'Months to years (regular video); Shorts behave like TikTok',
    boost: 'Click-through on the title/thumbnail, then watch time',
    cadence: '1× a week (or daily Shorts)',
    discovery: 'Pure search — put the words people type into your title & description',
    tip: 'Title for what people search; a clear thumbnail does half the work.',
  },
  linkedin: {
    reach: '~5–10% of connections',
    reachNote: 'Favors native posts over outbound links.',
    life: '~1–2 days, with a slow tail across the week',
    boost: 'Comments in the first hour and time spent reading',
    cadence: '2–4× a week',
    discovery: '3–5 niche hashtags + who you tag; people search by role',
    tip: 'Lead with a strong first line — the rest hides behind “…see more”.',
  },
  x: {
    reach: '~5% of followers',
    reachNote: 'A fast feed — reach is brief unless it gets reshared.',
    life: '~15–20 min half-life; mostly gone in a few hours',
    boost: 'Reposts and quote-posts; replies keep it alive',
    cadence: '1–5× a day',
    discovery: 'Hashtags + trending topics + keyword search',
    tip: 'Post more often and reply to others — frequency wins here.',
  },
  whatsapp: {
    reach: 'Direct — whoever you send or broadcast to',
    reachNote: 'No algorithm; read in the first hour or not at all.',
    life: 'Same-day; Status auto-clears after 24 hours',
    boost: 'A clear first line and one obvious next step',
    cadence: 'When you have something genuinely worth sending',
    discovery: 'No discovery — you bring the audience (your own contacts)',
    tip: 'Best for warm contacts and quick offers, not cold reach.',
  },
};

// Profile / local-SEO quick wins — [title, why]. Highest value for local trades.
const PROFILE_WINS = [
  ['Complete every field', 'bio, category, hours, service area — a half-empty profile looks closed'],
  ['Same name, address & phone everywhere', 'identical across every platform and Google — it’s how search learns to trust you'],
  ['One obvious next step', 'a Call, Book, or Message button — don’t make them hunt for how to reach you'],
  ['Pin your best post', 'it’s the first thing anyone sees when they tap your profile'],
  ['Keep Google reviews fresh', 'recent reviews outrank old ones and win the click'],
];

// The click → customer math — cited; quietly reinforces why a landing page matters.
const CLICK_TO_CUSTOMER = {
  headline: 'A click isn’t a customer — where they land decides',
  detail: 'Send every click to your generic homepage and ~1–2% convert (most bounce). A page that matches the post converts ~5–12% — several times more, from the very same clicks.',
  source: 'HubSpot / Unbounce',
  href: 'https://hubspot-state-of-marketing-2026.replit.app/',
};

// The review engine — local-business gold. Static.
const REVIEW_ENGINE = {
  headline: 'Turn happy customers into your best marketing',
  points: [
    'Ask every satisfied customer for a review — the day the job’s done, while it’s fresh',
    'Reply to every review, good or bad — future customers read how you handle them',
    'Turn a great review into a post — it’s social proof you didn’t have to write',
  ],
};

// Metrics that matter vs vanity — static.
const METRICS = {
  good: [
    ['Saves & shares', 'someone found it worth keeping or passing on'],
    ['Profile visits', 'curiosity about who you are'],
    ['DMs, calls & website clicks', 'the ones that actually become customers'],
  ],
  vanity: 'Likes & follower count alone — feel good, but don’t pay the bills',
};

module.exports = { PLATFORM_PLAYBOOK_INFO, PROFILE_WINS, CLICK_TO_CUSTOMER, REVIEW_ENGINE, METRICS };
